import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Chip, Grid, Alert, CircularProgress,
  Avatar, Button, LinearProgress, List, ListItem,
  ListItemText, ListItemAvatar, Tooltip, IconButton,
} from '@mui/material';
import {
  Home, BeachAccess, Receipt,
  Assignment, NotificationsActive,
  TrendingUp, EmojiEvents, Groups, Work, Refresh,
  ArrowForward, HourglassEmpty,
  TaskAlt,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

import { apiFetch as rawApiFetch } from '../../services/apiFetch';

function apiFetch(path: string) {
  return rawApiFetch(path, { headers: { 'Content-Type': 'application/json' } })
    .then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

function greet(name: string): string {
  const h = new Date().getHours();
  const time = h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
  return `${time}, ${name || 'there'} 👋`;
}

function timeAgo(dateStr: string): string {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const d = Math.floor(diff / 86400000);
  if (d === 0) return 'today';
  if (d === 1) return 'yesterday';
  if (d < 7) return `${d} days ago`;
  if (d < 30) return `${Math.floor(d / 7)}w ago`;
  return new Date(dateStr).toLocaleDateString('en-HK', { day: 'numeric', month: 'short' });
}

interface SectionHeaderProps { icon: React.ReactNode; title: string; link?: string; badge?: number; }
function SectionHeader({ icon, title, link, badge }: SectionHeaderProps) {
  const nav = useNavigate();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon}
        <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
        {badge ? <Chip size="small" label={badge} sx={{ bgcolor: '#C08A2E', color: '#fff', fontSize: '0.65rem', height: 18 }} /> : null}
      </Box>
      {link && (
        <Button size="small" endIcon={<ArrowForward />} sx={{ color: '#C08A2E', textTransform: 'none', fontSize: '0.75rem' }}
          onClick={() => nav(link)}>View all</Button>
      )}
    </Box>
  );
}

export default function EmployeeDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData]       = useState<any>({});
  const [now, setNow]         = useState(new Date());

  // Tick clock every minute
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  const employeeId = (user as any)?.employee?.id;

  const load = useCallback(async () => {
    setLoading(true);
    // NOTE: tiles for training, kudos/recognition, surveys, announcements and
    // certifications were removed — those backend modules are unregistered
    // (404) or have no controller (audit findings #9). Only routes verified to
    // exist against the backend controllers remain here.
    const results = await Promise.allSettled([
      apiFetch('/api/leave/balances/my-balances'),               // 0 leave balances
      apiFetch('/api/leave/requests?mine=true&status=pending'),  // 1 pending leave
      apiFetch('/api/payslips/my?limit=3'),                      // 2 recent payslips
      apiFetch('/api/policies?ackRequired=true&mine=true'),      // 3 policies to ack
      apiFetch('/api/goals?mine=true&limit=4'),                  // 4 my goals
      apiFetch('/api/wfh?mine=true&status=approved&thisWeek=true'), // 5 WFH this week
      employeeId ? apiFetch(`/api/employees/${employeeId}`) : Promise.resolve(null), // 6 my profile
      apiFetch('/api/performance/reviews/my-reviews?status=pending'), // 7 pending reviews
    ]);

    const get = (i: number) => { const r = results[i]; return r.status === 'fulfilled' ? (r as PromiseFulfilledResult<any>).value : null; };
    setData({
      leaveBalances:    get(0),
      pendingLeave:     get(1),
      payslips:         get(2),
      pendingPolicies:  get(3),
      goals:            get(4),
      wfhThisWeek:      get(5),
      profile:          get(6),
      pendingReviews:   get(7),
    });
    setLoading(false);
  }, [employeeId]);

  useEffect(() => { load(); }, [load]);

  const profile  = data.profile ?? {};
  const fullName = profile.firstName ? `${profile.firstName}` : (user as any)?.name?.split(' ')?.[0] ?? '';
  const dept     = profile.department?.name ?? '';
  const position = profile.position?.name ?? '';

  const balances      = Array.isArray(data.leaveBalances) ? data.leaveBalances : (data.leaveBalances?.data ?? []);
  const pendingLeave  = Array.isArray(data.pendingLeave) ? data.pendingLeave : (data.pendingLeave?.data ?? []);
  const payslips      = Array.isArray(data.payslips) ? data.payslips : (data.payslips?.data ?? []);
  const pendingPols   = Array.isArray(data.pendingPolicies) ? data.pendingPolicies : (data.pendingPolicies?.data ?? []);
  const goals         = Array.isArray(data.goals) ? data.goals : (data.goals?.data ?? []);
  const wfhDays       = Array.isArray(data.wfhThisWeek) ? data.wfhThisWeek : (data.wfhThisWeek?.data ?? []);
  const reviews       = Array.isArray(data.pendingReviews) ? data.pendingReviews : (data.pendingReviews?.data ?? []);

  const actionCount = pendingPols.length + reviews.length;

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ width: 56, height: 56, bgcolor: '#C08A2E', fontSize: '1.5rem' }}>
            {fullName?.[0]?.toUpperCase() ?? '?'}
          </Avatar>
          <Box>
            <Typography variant="h5" fontWeight={700}>{greet(fullName)}</Typography>
            <Typography variant="body2" color="text.secondary">
              {[position, dept].filter(Boolean).join(' · ')} &nbsp;·&nbsp;
              {now.toLocaleDateString('en-HK', { weekday: 'long', day: 'numeric', month: 'long' })}
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Refresh"><IconButton onClick={load}><Refresh /></IconButton></Tooltip>
      </Box>

      {/* Pending Actions alert */}
      {actionCount > 0 && (
        <Alert severity="warning" icon={<NotificationsActive />} sx={{ mb: 2 }}>
          <strong>{actionCount} action{actionCount > 1 ? 's' : ''} need your attention:</strong>{' '}
          {pendingPols.length > 0 && `${pendingPols.length} polic${pendingPols.length > 1 ? 'ies' : 'y'} to acknowledge · `}
          {reviews.length > 0 && `${reviews.length} performance review${reviews.length > 1 ? 's' : ''} pending`}
        </Alert>
      )}

      {/* Quick Actions row */}
      <Paper sx={{ p: 1.5, mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {[
          { label: 'Request Leave',   icon: <BeachAccess />,    path: '/leave'           },
          { label: 'WFH Request',     icon: <Home />,           path: '/wfh'             },
          { label: 'Expense Claim',   icon: <Receipt />,        path: '/expense-reports' },
          { label: 'My Payslips',     icon: <Work />,           path: '/payslip'         },
          { label: 'My Goals',        icon: <TrendingUp />,     path: '/goals'           },
          { label: 'Give Kudos',      icon: <EmojiEvents />,    path: '/recognition'     },
          { label: 'Help Desk',       icon: <Assignment />,     path: '/helpdesk'        },
          { label: 'Directory',       icon: <Groups />,         path: '/directory'       },
        ].map(a => (
          <Button key={a.label} variant="outlined" size="small" startIcon={a.icon}
            sx={{ borderColor: '#e0e0e0', color: '#37474f', textTransform: 'none',
              '&:hover': { borderColor: '#C08A2E', color: '#C08A2E', bgcolor: '#fff3e0' } }}
            onClick={() => nav(a.path)}>{a.label}</Button>
        ))}
      </Paper>

      <Grid container spacing={2}>
        {/* Left column */}
        <Grid size={{ xs: 12, md: 7 }}>
          {/* Leave Balances */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <SectionHeader icon={<BeachAccess color="action" />} title="Leave Balances" link="/leave" />
            {balances.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No leave balances on record.</Typography>
            ) : (
              <Grid container spacing={1}>
                {balances.slice(0, 6).map((b: any) => {
                  const avail = parseFloat(b.closingBalance ?? b.balance ?? 0);
                  const total = parseFloat(b.entitled ?? b.annualEntitlement ?? avail);
                  const pct   = total > 0 ? Math.min(100, Math.round((avail / total) * 100)) : 0;
                  const low   = pct < 20;
                  return (
                    <Grid size={{ xs: 6, sm: 4 }} key={b.id ?? b.leaveTypeId}>
                      <Box sx={{ p: 1.25, borderRadius: 1, border: `1px solid ${low ? '#ffcdd2' : '#e0e0e0'}`, bgcolor: low ? '#fff5f5' : 'inherit' }}>
                        <Typography variant="caption" color="text.secondary" noWrap>{b.leaveType?.name ?? b.name}</Typography>
                        <Typography variant="h6" fontWeight={700} color={low ? '#b71c1c' : '#37474f'}>
                          {avail.toFixed(1)}
                          <Typography component="span" variant="caption" color="text.secondary"> days</Typography>
                        </Typography>
                        <LinearProgress variant="determinate" value={pct}
                          sx={{ height: 4, borderRadius: 2, mt: 0.5,
                            '& .MuiLinearProgress-bar': { bgcolor: low ? '#ef5350' : '#C08A2E' } }} />
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            )}
            {pendingLeave.length > 0 && (
              <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {pendingLeave.map((l: any) => (
                  <Chip key={l.id} size="small" icon={<HourglassEmpty />}
                    label={`${l.leaveType?.name ?? 'Leave'} pending: ${l.totalDays}d`}
                    sx={{ bgcolor: '#fff3e0', color: '#e65100' }} />
                ))}
              </Box>
            )}
          </Paper>

          {/* Goals */}
          {goals.length > 0 && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <SectionHeader icon={<TrendingUp color="action" />} title="My Goals" link="/goals" />
              <List dense disablePadding>
                {goals.slice(0, 4).map((g: any) => (
                  <ListItem key={g.id} disableGutters sx={{ py: 0.5 }}>
                    <ListItemText
                      primary={<Typography variant="body2" fontWeight={600} noWrap>{g.title}</Typography>}
                      secondary={
                        <Box sx={{ mt: 0.25 }}>
                          <LinearProgress variant="determinate" value={parseFloat(g.progress ?? 0)}
                            sx={{ height: 5, borderRadius: 2, bgcolor: '#f0f0f0',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: g.status === 'at_risk' ? '#f57f17' : g.status === 'completed' ? '#2e7d32' : '#C08A2E',
                              } }} />
                          <Typography variant="caption" color="text.secondary">
                            {parseFloat(g.progress ?? 0).toFixed(0)}% · {g.status?.replace('_', ' ')}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}

          {/* Recent Payslips */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <SectionHeader icon={<Work color="action" />} title="Recent Payslips" link="/payslip" />
            {payslips.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No payslips available yet.</Typography>
            ) : (
              <List dense disablePadding>
                {payslips.slice(0, 3).map((p: any) => (
                  <ListItem key={p.id} disableGutters sx={{ py: 0.5 }}>
                    <ListItemAvatar sx={{ minWidth: 36 }}>
                      <Avatar sx={{ width: 28, height: 28, bgcolor: '#e8f5e9', color: '#2e7d32', fontSize: '0.7rem' }}>
                        <Receipt fontSize="small" />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={<Typography variant="body2">{p.period ?? p.payPeriod ?? `${p.month ?? ''} ${p.year ?? ''}`}</Typography>}
                      secondary={<Typography variant="caption" color="text.secondary">{timeAgo(p.createdAt)}</Typography>}
                    />
                    {(p.netPay ?? p.netAmount) && (
                      <Typography variant="body2" fontWeight={700} color="#2e7d32">
                        HKD {parseFloat(p.netPay ?? p.netAmount).toLocaleString('en-HK', { maximumFractionDigits: 0 })}
                      </Typography>
                    )}
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>

        </Grid>

        {/* Right column */}
        <Grid size={{ xs: 12, md: 5 }}>
          {/* Actions required */}
          {actionCount > 0 && (
            <Paper sx={{ p: 2, mb: 2, border: '1.5px solid #ffe082' }}>
              <SectionHeader icon={<TaskAlt sx={{ color: '#f57f17' }} />} title="Action Required" badge={actionCount} />
              {pendingPols.slice(0, 2).map((p: any) => (
                <Alert key={p.id} severity="warning" sx={{ mb: 1, py: 0 }}
                  action={<Button size="small" onClick={() => nav('/policies')}>Read</Button>}>
                  Policy to acknowledge: {p.title}
                </Alert>
              ))}
              {reviews.slice(0, 1).map((r: any) => (
                <Alert key={r.id} severity="warning" sx={{ mb: 1, py: 0 }}
                  action={<Button size="small" onClick={() => nav('/performance')}>Review</Button>}>
                  Performance review pending
                </Alert>
              ))}
            </Paper>
          )}

          {/* WFH & status this week */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <SectionHeader icon={<Home color="action" />} title="This Week" link="/wfh" />
            {wfhDays.length > 0 ? (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {wfhDays.map((w: any) => (
                  <Chip key={w.id} size="small" icon={<Home />}
                    label={new Date(w.date).toLocaleDateString('en-HK', { weekday: 'short', day: 'numeric' })}
                    sx={{ bgcolor: '#e3f2fd', color: '#1565c0' }} />
                ))}
                <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', ml: 0.5 }}>
                  WFH day{wfhDays.length > 1 ? 's' : ''}
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">No WFH days approved this week.</Typography>
            )}
            <Button variant="text" size="small" sx={{ mt: 1, color: '#C08A2E', textTransform: 'none' }}
              onClick={() => nav('/wfh')}>Request WFH →</Button>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
